import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Toggle from '../components/Toggle';
import QtyStepper from '../components/QtyStepper';
import NumInput from '../components/NumInput';
import { EditIcon, TrashIcon } from '../components/ActionIcons';

const BRAND_GREEN = '#1C3D2E';
const CORAL = '#B0603C';
const CORAL_BG = '#FFE7DD';
const AMBER = '#7E5D00';
const AMBER_BG = '#F9EBCF';
const GRAY_TEXT = '#5E6561';
const BORDER = '#EBEDEC';
const CARD_BG = '#FFFFFF';

const LOW_STOCK_THRESHOLD = 10;

const FALLBACK_ICON = (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7l9-4 9 4-9 4-9-4z" />
    <path d="M3 7v10l9 4 9-4V7" />
    <path d="M12 11v10" />
  </svg>
);

const API_BASE = (typeof window !== 'undefined' && window.location)
  ? window.location.origin
  : '';

function resolveImg(item) {
  if (!item.image_url) return null;
  return item.image_url.startsWith('http') ? item.image_url : `${API_BASE}${item.image_url}`;
}

function statusFor(qty) {
  if (qty === 0) return 'out';
  if (qty <= LOW_STOCK_THRESHOLD) return 'low';
  return 'ok';
}

const DEFAULT_CATALOG_LABELS = { products: 'Стебли', bouquets: 'Букеты' };

function PackagingPage({ showToast, embedded = false, createTrigger, onCreateHandled }) {
  const [packaging, setPackaging] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Названия разделов витрины («Стебли»/«Букеты» могут быть переименованы владельцем,
  // например в «Сеты») — для подписей чекбоксов «доступна для».
  const [catalogLabels, setCatalogLabels] = useState(DEFAULT_CATALOG_LABELS);

  useEffect(() => { loadPackaging(); }, []);

  useEffect(() => {
    api.getSettings().then(s => {
      setCatalogLabels({
        products: s.catalog_label_products || DEFAULT_CATALOG_LABELS.products,
        bouquets: s.catalog_label_bouquets || DEFAULT_CATALOG_LABELS.bouquets,
      });
    }).catch(() => {});
  }, []);

  // Внешний триггер из ProductsPage по кнопке "+ Добавить упаковку"
  useEffect(() => {
    if (createTrigger) {
      setCreatingNew(true);
      onCreateHandled?.();
    }
  }, [createTrigger, onCreateHandled]);

  const loadPackaging = async () => {
    setIsLoading(true);
    try {
      // Админу показываем всё (включая скрытые)
      const data = await api.getPackagingInventory(true);
      setPackaging(data);
    } catch {
      showToast('Ошибка загрузки упаковок', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const updateLocal = (type, patch) => {
    setPackaging(prev => prev.map(p => p.type === type ? { ...p, ...patch } : p));
  };

  const adjustQty = async (type, delta) => {
    const item = packaging.find(p => p.type === type);
    if (!item) return;
    const newQty = Math.max(0, item.quantity + delta);
    if (newQty === item.quantity) return;
    updateLocal(type, { quantity: newQty });
    try {
      await api.updatePackaging(type, { quantity: newQty });
    } catch {
      showToast('Ошибка сохранения', 'error');
      loadPackaging();
    }
  };

  const setQty = async (type, newQty) => {
    if (isNaN(newQty) || newQty < 0) {
      showToast('Некорректное количество', 'error');
      return;
    }
    updateLocal(type, { quantity: newQty });
    try {
      await api.updatePackaging(type, { quantity: newQty });
    } catch {
      showToast('Ошибка сохранения', 'error');
      loadPackaging();
    }
  };

  const toggleHidden = async (type) => {
    const item = packaging.find(p => p.type === type);
    if (!item) return;
    const next = !item.is_hidden;
    updateLocal(type, { is_hidden: next });
    try {
      await api.updatePackaging(type, { is_hidden: next });
      showToast(next ? 'Упаковка скрыта на сторфронте' : 'Упаковка показана на сторфронте', 'success');
    } catch {
      showToast('Ошибка сохранения', 'error');
      loadPackaging();
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.deletePackaging(confirmDelete.type);
      showToast('Упаковка удалена', 'success');
      setConfirmDelete(null);
      loadPackaging();
    } catch (e) {
      showToast(e.message || 'Не удалось удалить', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className={embedded ? '' : 'page-container'}>
        {!embedded && <div className="page-header"><h1>Управление упаковками</h1></div>}
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'page-container'}>
      {!embedded && (
        <div className="page-header">
          <h1>Управление упаковками</h1>
        </div>
      )}

      <div style={{ display: 'grid', gap: 12, maxWidth: 720, marginTop: 16 }}>
        {packaging.map(item => (
          <PackagingCard
            key={item.type}
            item={item}
            catalogLabels={catalogLabels}
            onAdjust={adjustQty}
            onSetQuantity={(qty) => setQty(item.type, qty)}
            onEdit={() => setEditingItem(item)}
            onDelete={() => setConfirmDelete({ type: item.type, name: item.name || item.type })}
            onToggleHidden={() => toggleHidden(item.type)}
          />
        ))}
      </div>

      {packaging.length === 0 && (
        <div style={{
          textAlign: 'center', padding: 40, marginTop: 16,
          background: CARD_BG, border: `1px solid ${BORDER}`, '--ch': '8px', '--stroke': (BORDER),
          color: GRAY_TEXT,
        }}>
          Нет упаковок. Нажми «+ Добавить упаковку».
        </div>
      )}

      {(editingItem || creatingNew) && (
        <PackagingFormModal
          item={editingItem}
          isNew={creatingNew}
          catalogLabels={catalogLabels}
          maxSortOrder={Math.max(0, ...packaging.map(p => p.sort_order || 0))}
          onClose={() => { setEditingItem(null); setCreatingNew(false); }}
          onSaved={() => { setEditingItem(null); setCreatingNew(false); loadPackaging(); }}
          showToast={showToast}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Удалить упаковку?"
          message={`Упаковка «${confirmDelete.name}» будет удалена. История заказов сохранится. Если есть активные заказы с этой упаковкой — удаление будет заблокировано.`}
          confirmText="Удалить"
          cancelText="Отмена"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
          variant="danger"
        />
      )}
    </div>
  );
}


function PackagingCard({ item, catalogLabels, onAdjust, onSetQuantity, onEdit, onDelete, onToggleHidden }) {
  const status = statusFor(item.quantity);
  const countColor = status === 'out' ? CORAL : status === 'low' ? AMBER : BRAND_GREEN;
  const [editingQty, setEditingQty] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  const imgSrc = resolveImg(item);
  const isHidden = !!item.is_hidden;

  const startEdit = () => {
    setDraft(String(item.quantity));
    setEditingQty(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const commit = () => {
    const n = parseInt(draft);
    setEditingQty(false);
    if (!isNaN(n) && n !== item.quantity) onSetQuantity(n);
  };

  return (
    <div style={{
      background: CARD_BG, border: `1px solid ${BORDER}`,
      '--ch': '8px', '--stroke': (BORDER), padding: 14,
      // flexWrap: на телефоне кнопки уходят второй строкой, а не за правый край
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      opacity: isHidden ? 0.7 : 1,
    }}>
      {/* Image — read-only превью. Загрузка/замена — в edit-модалке. */}
      <div style={{ width: 100, height: 100, flexShrink: 0 }}>
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={item.name || item.type}
            style={{
              width: 100, height: 100, objectFit: 'cover',
              '--ch': '8px', '--stroke': (BORDER), border: `1px solid ${BORDER}`,
            }}
          />
        ) : (
          <div style={{
            width: 100, height: 100, '--ch': '8px', '--stroke': (BORDER),
            background: '#F4F4EF', border: `1px solid ${BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: GRAY_TEXT,
          }}>
            {FALLBACK_ICON}
          </div>
        )}
      </div>

      {/* Name + meta. База 200px: при flex: 1 (база 0) на телефоне название сжималось
          до 6–22 px, а в строку с ним влезали «−», количество и «+» */}
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1C3D2E', lineHeight: 1.3 }}>
            {item.name || item.type}
          </div>
          {isHidden && (
            <span style={{
              padding: '2px 8px', '--ch': '4px',
              background: '#EEEEEC', color: GRAY_TEXT, fontSize: 11, fontWeight: 500,
            }}>
              скрыта
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: GRAY_TEXT, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span>Порядок: {item.sort_order}</span>
          <span style={{ padding: '2px 8px', '--ch': '4px', background: '#EFFAF4', color: BRAND_GREEN, fontWeight: 500 }}>
            для: {[
              item.for_products !== false && catalogLabels.products,
              item.for_bouquets && catalogLabels.bouquets,
            ].filter(Boolean).join(', ') || '—'}
          </span>
          {item.flowwow_default && (
            <span style={{ padding: '2px 8px', '--ch': '4px', background: '#FCE4EC', color: '#C2185B', fontWeight: 600 }}>
              Flowwow
            </span>
          )}
          {status === 'out' && (
            <span style={{ padding: '2px 8px', '--ch': '4px', background: CORAL_BG, color: CORAL, fontWeight: 500 }}>
              недоступна для заказов
            </span>
          )}
          {status === 'low' && (
            <span style={{ padding: '2px 8px', '--ch': '4px', background: AMBER_BG, color: AMBER, fontWeight: 500 }}>
              низкий остаток
            </span>
          )}
        </div>
        {/* Hide toggle */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <Toggle checked={!isHidden} onChange={onToggleHidden} />
          <span style={{ fontSize: 12, color: GRAY_TEXT }}>
            {isHidden ? 'Показать на сторфронте' : 'Показывать на сторфронте'}
          </span>
        </div>
      </div>

      {/* −/+, карандаш, корзина — общий .btn-icon, размер 36 (UX-аудит 25.10) */}
      <button
        type="button"
        className="btn-icon"
        onClick={() => onAdjust(item.type, -1)}
        disabled={item.quantity === 0}
        title="−1"
        style={{
          width: 36, height: 36, fontSize: 18, fontWeight: 600, lineHeight: 1,
          ...(item.quantity === 0 ? { opacity: 0.45, cursor: 'not-allowed' } : null),
        }}
      >−</button>

      {editingQty ? (
        <input
          ref={inputRef} type="number" inputMode="numeric" value={draft} min="0"
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditingQty(false);
          }}
          className="no-spinner"
          style={{
            width: 72, height: 36, textAlign: 'center',
            fontSize: 18, fontWeight: 700, color: countColor,
            border: `1px solid ${BRAND_GREEN}`, '--ch': '6px', '--stroke': (BRAND_GREEN),
            outline: 'none', padding: 0, MozAppearance: 'textfield',
          }}
        />
      ) : (
        <button
          onClick={startEdit} title="Кликните чтобы изменить"
          style={{
            width: 72, height: 36, padding: 0, background: 'transparent',
            border: '1px solid transparent', '--ch': '6px', '--stroke': 'transparent', cursor: 'text',
            fontSize: 22, fontWeight: 700, color: countColor, lineHeight: 1,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.setProperty('--stroke', BORDER); }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.setProperty('--stroke', 'transparent'); }}
        >
          {item.quantity}
        </button>
      )}

      <button
        type="button"
        className="btn-icon"
        onClick={() => onAdjust(item.type, 1)}
        title="+1"
        style={{ width: 36, height: 36, fontSize: 18, fontWeight: 600, lineHeight: 1 }}
      >+</button>

      <button
        type="button"
        className="btn-icon"
        onClick={onEdit}
        title="Редактировать"
        aria-label="Редактировать"
        style={{ width: 36, height: 36 }}
      >
        <EditIcon />
      </button>

      <button
        type="button"
        className="btn-icon is-danger"
        onClick={onDelete}
        title="Удалить упаковку"
        aria-label="Удалить упаковку"
        style={{ width: 36, height: 36 }}
      >
        <TrashIcon />
      </button>
    </div>
  );
}


function PackagingFormModal({ item, isNew, catalogLabels, maxSortOrder, onClose, onSaved, showToast }) {
  const [name, setName] = useState(item?.name || '');
  const [description, setDescription] = useState(item?.description || '');
  const [quantity, setQuantity] = useState(item?.quantity ?? 0);
  const [cost, setCost] = useState(item?.cost ?? 0);
  const [sortOrder, setSortOrder] = useState(item?.sort_order ?? (maxSortOrder + 1));
  const [forProducts, setForProducts] = useState(item ? item.for_products !== false : true);
  const [forBouquets, setForBouquets] = useState(item ? !!item.for_bouquets : false);
  const [flowwowDefault, setFlowwowDefault] = useState(item ? !!item.flowwow_default : false);
  const [saving, setSaving] = useState(false);

  // Image state:
  // - existing image_url из item.image_url (только для edit)
  // - pendingFile — File выбранный локально, но ещё не загруженный (preview через ObjectURL)
  // - imageRemoved — true если в edit-режиме нажали удалить (применить при сохранении)
  const [pendingFile, setPendingFile] = useState(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const fileRef = useRef(null);
  const currentImgSrc = !imageRemoved && item?.image_url ? resolveImg(item) : null;
  const previewSrc = pendingFile ? URL.createObjectURL(pendingFile) : currentImgSrc;

  const handleSelectFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setImageRemoved(false);
    if (e.target) e.target.value = '';
  };

  const handleRemoveImage = () => {
    setPendingFile(null);
    setImageRemoved(true);
  };

  const persistImage = async (typeKey) => {
    // 1. Если был помечен на удаление и ничего нового не выбрано — DELETE
    if (imageRemoved && !pendingFile && item?.image_url) {
      try { await api.deletePackagingImage(typeKey); }
      catch { /* мягко глотаем — основные данные уже сохранены */ }
    }
    // 2. Если выбран новый файл — POST upload
    if (pendingFile) {
      try { await api.uploadPackagingImage(typeKey, pendingFile); }
      catch (e) { showToast(e.message || 'Не удалось загрузить фото', 'error'); }
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showToast('Название обязательно', 'error');
      return;
    }
    if (!forProducts && !forBouquets) {
      showToast('Отметьте хотя бы один раздел, для которого доступна упаковка', 'error');
      return;
    }
    setSaving(true);
    try {
      let typeKey = item?.type;
      if (isNew) {
        if (quantity < 0) {
          showToast('Количество не может быть отрицательным', 'error');
          setSaving(false);
          return;
        }
        const created = await api.createPackaging({
          name: name.trim(),
          description: description.trim() || null,
          quantity,
          cost: Number(cost) || 0,
          sort_order: sortOrder,
          for_products: forProducts,
          for_bouquets: forBouquets,
          flowwow_default: flowwowDefault,
        });
        typeKey = created.type;
        // Фото можно загрузить только после создания (нужен type)
        await persistImage(typeKey);
        showToast('Упаковка создана', 'success');
      } else {
        await api.updatePackaging(item.type, {
          name: name.trim(),
          description: description.trim() || null,
          cost: Number(cost) || 0,
          sort_order: sortOrder,
          for_products: forProducts,
          for_bouquets: forBouquets,
          flowwow_default: flowwowDefault,
        });
        await persistImage(typeKey);
        showToast('Сохранено', 'success');
      }
      onSaved();
    } catch (e) {
      showToast(e.message || 'Ошибка сохранения', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isNew ? 'Новая упаковка' : `Редактировать «${item.name || item.type}»`}
      onClose={onClose}
      confirmOverlayClose
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Отмена</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Image upload */}
        <Field label="Фото" hint="Один квадратный кадр (auto-resize до 400×400). Показывается на сторфронте.">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ position: 'relative', width: 120, height: 120, flexShrink: 0 }}>
              {previewSrc ? (
                <>
                  <img
                    src={previewSrc}
                    alt="preview"
                    style={{
                      width: 120, height: 120, objectFit: 'cover',
                      '--ch': '8px', '--stroke': (BORDER), border: `1px solid ${BORDER}`,
                    }}
                  />
                  {/* «×» на фото — удаление, красным, не терракотой (UX-аудит 25.10) */}
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    title="Удалить фото"
                    style={{
                      position: 'absolute', top: 6, right: 6,
                      width: 22, height: 22, borderRadius: '50%', border: 'none',
                      background: 'rgba(203,77,69, 0.95)', color: '#fff',
                      cursor: 'pointer', fontSize: 14, lineHeight: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  >×</button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  style={{
                    width: 120, height: 120, border: `2px dashed ${BORDER}`, '--ch': '8px', '--stroke': BORDER, '--bw': '2px',
                    background: '#fff', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    color: BRAND_GREEN, gap: 4,
                  }}
                >
                  {FALLBACK_ICON}
                  <span style={{ fontSize: 12, color: GRAY_TEXT }}>+ фото</span>
                </button>
              )}
            </div>
            {previewSrc && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => fileRef.current?.click()}
              >
                Заменить фото
              </button>
            )}
            <input
              ref={fileRef} type="file" accept="image/*"
              style={{ display: 'none' }} onChange={handleSelectFile}
            />
          </div>
        </Field>

        <Field label="Название" hint="Отображается в чекауте сторфронта">
          <input
            type="text" value={name}
            onChange={e => setName(e.target.value)}
            placeholder="например, Эко-сумка"
            autoFocus
            style={inputStyle()}
          />
        </Field>
        <Field label="Описание" hint="Опционально">
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={2}
            style={{ ...inputStyle(), resize: 'vertical', fontFamily: 'inherit' }}
          />
        </Field>
        <Field
          label="Доступна для"
          hint={`В чекауте упаковка предлагается только для отмеченных разделов. Чтобы у раздела «${catalogLabels.bouquets}» появился выбор упаковки, отметьте для него хотя бы одну.`}
        >
          <div style={{ display: 'flex', gap: 18, paddingTop: 2 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={forProducts}
                onChange={e => setForProducts(e.target.checked)}
              />
              {catalogLabels.products}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={forBouquets}
                onChange={e => setForBouquets(e.target.checked)}
              />
              {catalogLabels.bouquets}
            </label>
          </div>
        </Field>
        <Field
          label="Коробка для заказов Flowwow"
          hint="Заказы с Flowwow уходят в этой коробке: при создании заказа списывается 1 шт. Такая коробка может быть только одна — галочка снимется с прежней."
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer', paddingTop: 2 }}>
            <input type="checkbox" checked={flowwowDefault} onChange={e => setFlowwowDefault(e.target.checked)} />
            Использовать для заказов Flowwow
          </label>
        </Field>
        <div style={{ display: 'flex', gap: 12 }}>
          {isNew && (
            <Field label="Количество в наличии" style={{ flex: 1 }}>
              <QtyStepper value={quantity} min={0} onChange={setQuantity} />
            </Field>
          )}
          <Field label="Себестоимость, ₽" hint="закупочная цена за единицу (для аналитики маржи)" style={{ flex: 1 }}>
            <input
              type="number" min="0" step="0.01" value={cost}
              onChange={e => setCost(e.target.value)}
              style={inputStyle()}
            />
          </Field>
          <Field label="Порядок (1=слева, 2=справа, 3=снизу слева...)" style={{ flex: 1 }}>
            <NumInput
              integer min={1} fallback={1} value={sortOrder}
              onChange={setSortOrder}
              style={inputStyle()}
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, hint, children, style }) {
  return (
    <div style={style}>
      <div style={{ fontSize: 13, fontWeight: 500, color: '#1C3D2E', marginBottom: 4 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 11, color: GRAY_TEXT, marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function inputStyle() {
  return {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${BORDER}`,
    '--ch': '5px', '--stroke': (BORDER),
    fontSize: 14,
    outline: 'none',
    background: '#fff',
    color: '#1C3D2E',
    boxSizing: 'border-box',
  };
}

export default PackagingPage;
